import React, { useState, useEffect } from 'react';
import {
    Portal, TextField, Checkbox, FormControlLabel,
    MenuItem, Select, CircularProgress,
    Tooltip, IconButton,
} from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import CustomMonthDatePicker from '../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import CustomDatePicker from '../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutocompleteSelectFilter from '../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter';
import { getVatValue } from '../../Utils/GetVatValue';
import s from './AddNewCollectionItem.module.scss';

const PaymentTypeOptions = [
    { name: "CreditCard", value: "0" },
    { name: "Check", value: "1" },
    { name: "BankTransfer", value: "2" },
    { name: "Cash", value: "3" }
];

const CHECK_TYPE_VALUE = PaymentTypeOptions.find(o => o.name === 'Check').value;

const fieldSx = {
    height: '40px',
    padding: '0 12px',
    borderRadius: '10px',
    background: '#f5f6fd',
    width: '100%',
    border: '1px solid #e5e7f6',
    fontSize: '0.875rem',
    boxSizing: 'border-box',
    transition: 'border-color .15s ease, box-shadow .15s ease, background .15s ease',
    '&:hover': { borderColor: '#c9cff5' },
    '&.Mui-focused, &:focus-within': {
        borderColor: '#304FFF',
        boxShadow: '0 0 0 3px rgba(48, 79, 255, 0.12)',
        background: '#fff',
    },
};

const baseInputProps = (direction) => ({
    disableUnderline: true,
    className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
    sx: fieldSx,
});

const baseInputStyle = (direction) => ({
    style: { textAlign: direction === 'ltr' ? 'left' : 'right' }
});

const selectSx = {
    ...fieldSx,
    display: 'flex',
    alignItems: 'center',
    '& .MuiSelect-select': { padding: '0 !important', background: 'transparent !important' },
};

/** Labeled form cell inside a `.grid`; `full` spans both columns. */
const Field = ({ title, required = false, full = false, children }) => (
    <div className={`${s.field}${full ? ` ${s.fieldFull}` : ''}`}>
        {title && (
            <span className={s.fieldLabel}>
                {title}
                {required && <span className={s.required}>*</span>}
            </span>
        )}
        {children}
    </div>
);

const VatToggle = ({ p, vatRate, update, t, lang, isEditMode }) => {
    const raw = parseFloat(p.amount) || 0;           // value in the input / DB
    const includes = p.priceIncludingVAT ?? true;         // current radio state

    const fmt = v =>
        Number(v || 0).toLocaleString(
            ['he', 'ar'].includes(lang) ? 'he-IL' : 'en-US',
            { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    /* ---------- derive net & gross ---------- */
    let net, gross;

    if (isEditMode) {
        gross = raw;                  // DB amount is gross
        net = raw * (1 + vatRate);
    } else {
        net = raw;
        gross = raw * (1 + vatRate);
    }

    /* ---------- amount that will be saved for each option ---------- */
    let savedIfIncl, savedIfExcl;

    if (isEditMode) {
        savedIfIncl = gross;   // 1180
        savedIfExcl = net;     // 1180 → (1000 + VAT)
    } else {
        savedIfIncl = net;     // 1000 (1000)
        savedIfExcl = gross;   // 1000 (1180)
    }

    const options = [
        { value: true, title: t('PriceIncludingVAT'), saved: savedIfIncl },
        { value: false, title: t('PriceExcludingVAT'), saved: savedIfExcl },
    ];

    return (
        <div className={s.vatOptions} role="radiogroup">
            {options.map((o) => {
                const active = includes === o.value;
                return (
                    <button
                        key={String(o.value)}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className={`${s.vatOption}${active ? ` ${s.vatOptionActive}` : ''}`}
                        onClick={() => update('priceIncludingVAT', o.value)}
                    >
                        <span className={s.radioDot} />
                        <span className={s.vatOptionBody}>
                            <span className={s.vatOptionTitle}>{o.title}</span>
                            <span className={s.vatOptionValue}>
                                {t('PriceIncludingVAT')}: {fmt(o.saved)}
                            </span>
                        </span>
                    </button>
                );
            })}
        </div>
    );
};


const AddNewCollectionItem = ({
    isOpen,
    onClose,
    newItem,
    handleInputChange,
    handleAddItem,
    handleEditItem,
    itemType,
    isEditMode = false,
    handleDeleteItem,
    loading = false,
    leadingBusiness = false,
    clientOptions = [],
    viewInvoice = () => { },
    viewReceipt = () => { },
    onDeletePayment = null,
    onCancelPaymentManually = null,
    onSetPaymentAmountNegative = null,
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();

    /* ---------- local state ---------- */
    const [form, setForm] = useState({ ...newItem });
    const [hasEndDate, setHasEndDate] = useState(false);
    const [vatRate, setVatRate] = useState(getVatValue(form.date || form.startDate));

    // New state for multiple payments
    const [multiplePayments, setMultiplePayments] = useState([
        {
            id: 1,
            type: '',
            date: dayjs(),
            amount: '',
            description: '',
            reference: '',
            bankName: '',
            bankCode: '',
            bankBranchCode: '',
            supplierAccountNumber: '',
            priceIncludingVAT: true
        }
    ]);
    const [nextPaymentId, setNextPaymentId] = useState(2);
    const [selectedClientForMultiple, setSelectedClientForMultiple] = useState(null);

    useEffect(() => {
        setForm({ ...newItem, priceIncludingVAT: true });
    }, [newItem]);

    useEffect(() => {
        if (form.date || form.startDate) {
            setVatRate(getVatValue(form.date || form.startDate));
        }
    }, [form.date, form.startDate]);

    // When editing monthlyCharges with an endDate, show the "Include End Date" checkbox as selected
    useEffect(() => {
        if (itemType === 'monthlyCharges') {
            setHasEndDate(!!form.endDate);
        }
    }, [itemType, form.endDate]);

    // Pre-populate selectedClientForMultiple for non-leading business clients
    useEffect(() => {
        if (!leadingBusiness && clientOptions.length > 0) {
            // Find the current client in the options
            const currentClient = clientOptions.find(option => option.value === form.caseId);
            if (currentClient) {
                setSelectedClientForMultiple(currentClient);
            }
        }
    }, [leadingBusiness, clientOptions, form.caseId]);

    const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    // Reset function to clear all fields
    const resetForm = () => {
        setForm({ ...newItem, priceIncludingVAT: true });
        setHasEndDate(false);
        setMultiplePayments([
            {
                id: 1,
                type: '',
                date: dayjs(),
                amount: '',
                description: '',
                reference: '',
                bankName: '',
                bankCode: '',
                bankBranchCode: '',
                supplierAccountNumber: '',
                priceIncludingVAT: true
            }
        ]);
        setNextPaymentId(2);
        setSelectedClientForMultiple(null);
    };

    // Handle modal close with reset
    const handleClose = () => {
        resetForm();
        onClose();
    };

    const L = baseInputProps(direction);
    const IS = baseInputStyle(direction);

    // Helper function to update multiple payments
    const updateMultiplePayment = (index, field, value) => {
        setMultiplePayments(prev => prev.map((payment, i) =>
            i === index ? { ...payment, [field]: value } : payment
        ));
    };

    // Add new payment row
    const addPaymentRow = () => {
        setMultiplePayments(prev => [
            {
                id: nextPaymentId,
                type: '',
                date: dayjs(),
                amount: '',
                description: '',
                reference: '',
                bankName: '',
                bankCode: '',
                bankBranchCode: '',
                supplierAccountNumber: '',
                priceIncludingVAT: true
            },
            ...prev
        ]);
        setNextPaymentId(prev => prev + 1);
    };

    // Remove payment row
    const removePaymentRow = (paymentId) => {
        if (multiplePayments.length > 1) {
            setMultiplePayments(prev => prev.filter(payment => payment.id !== paymentId));
        }
    };

    const buildPayload = () => {
        const raw = parseFloat(form.amount) || 0;
        const includes = form.priceIncludingVAT ?? true;
        const addMode = !isEditMode;

        const net = isEditMode ? raw * (1 + vatRate) : raw;
        const gross = isEditMode ? raw : raw * (1 + vatRate);

        const amountOut = includes
            ? (addMode ? net : gross)
            : (addMode ? gross : net);

        return { ...form, amount: amountOut, priceIncludingVAT: includes };
    };

    // Build payload for multiple payments
    const buildMultiplePaymentsPayload = () => {
        return multiplePayments.map(payment => {
            const raw = parseFloat(payment.amount) || 0;
            const includes = payment.priceIncludingVAT ?? true;

            const net = raw;
            const gross = raw * (1 + vatRate);

            const amountOut = includes ? net : gross;

            // For leading business, use selectedClientForMultiple, otherwise use form.caseId (client's caseId)
            const caseId = leadingBusiness ? (selectedClientForMultiple?.value || form.caseId) : form.caseId;

            return {
                ...payment,
                amount: amountOut,
                priceIncludingVAT: includes,
                caseId: caseId
            };
        });
    };

    // Validation for multiple payments
    const validateMultiplePayments = () => {
        // For leading business, require selectedClientForMultiple
        // For non-leading business, we don't need to validate client selection since we use the current client's caseId
        const hasValidClient = leadingBusiness ? selectedClientForMultiple : true;
        if (!hasValidClient) return false;

        return multiplePayments.every(payment =>
            payment.amount &&
            payment.description &&
            payment.type !== undefined &&
            payment.type !== ''
        );
    };

    /* ---------- shared field renderers ---------- */
    const renderTextField = (value, onChange, { type } = {}) => (
        <TextField
            fullWidth
            variant="standard"
            type={type}
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            InputProps={L}
            inputProps={IS}
        />
    );

    const renderTypeSelect = (value, onChange) => (
        <Select
            value={value?.toString() || ''}
            onChange={(e) => onChange(e.target.value)}
            variant="standard"
            disableUnderline
            displayEmpty
            sx={selectSx}
            renderValue={(v) => {
                const opt = PaymentTypeOptions.find(o => o.value === v);
                return opt ? t(opt.name) : <span className={s.muted}>{t('Select')}</span>;
            }}
            MenuProps={{ sx: { zIndex: 1400 } }}
        >
            {PaymentTypeOptions.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ direction }}>
                    {t(o.name)}
                </MenuItem>
            ))}
        </Select>
    );

    const renderClientSelect = (selected, onChange) => (
        <div className={s.clientSelect}>
            <AutocompleteSelectFilter
                key={selected?.value ?? 'empty'}
                options={clientOptions}
                selectedValues={selected}
                onChange={onChange}
                placeholder={t('SelectCustomer')}
                isMultiple={false}
                direction={direction}
                searchable
                portalMenu
                portalZIndex={1400}
                clearable
            />
        </div>
    );

    const renderVatSection = (p, onUpdate, editMode, showNote = true) => (
        <Field title={t('VATOption')} full>
            <VatToggle
                p={p}
                vatRate={vatRate}
                update={onUpdate}
                t={t}
                lang={i18n.language}
                isEditMode={editMode}
            />
            {showNote && (
                <div className={s.note}>
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    {t('SavedAmountIncludesVAT')}
                </div>
            )}
        </Field>
    );

    const renderCheckFields = (p, onUpdate) => (
        <>
            <h4 className={s.subSectionTitle}>{t('Check')}</h4>
            <Field title={t('BankName')}>
                {renderTextField(p.bankName, v => onUpdate('bankName', v))}
            </Field>
            <Field title={t('BankCode')}>
                {renderTextField(p.bankCode, v => onUpdate('bankCode', v))}
            </Field>
            <Field title={t('BankBranchCode')}>
                {renderTextField(p.bankBranchCode, v => onUpdate('bankBranchCode', v))}
            </Field>
            <Field title={t('SupplierAccountNumber')}>
                {renderTextField(p.supplierAccountNumber, v => onUpdate('supplierAccountNumber', v))}
            </Field>
        </>
    );

    /* ---------- renderFields ---------- */
    const renderFields = () => {
        switch (itemType) {
            /* ───────── MONTHLY CHARGES ───────── */
            case 'monthlyCharges':
                return (
                    <div className={s.section}>
                        <div className={s.grid}>
                            <Field title={t('StartDate')}>
                                <CustomMonthDatePicker
                                    date={form.startDate ? form.startDate : null}
                                    onDateChange={d => d && update('startDate', dayjs(d).date(10))}
                                    height="40px"
                                />
                            </Field>

                            <Field title={t('EndDate')}>
                                <div className={s.endDateRow}>
                                    <FormControlLabel
                                        sx={{ m: 0, flexShrink: 0 }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={hasEndDate}
                                                onChange={e => {
                                                    setHasEndDate(e.target.checked);
                                                    if (!e.target.checked) update('endDate', null);
                                                }}
                                            />
                                        }
                                        label={<span className={s.muted}>{t('IncludeEndDate')}</span>}
                                    />
                                    {hasEndDate && (
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <CustomMonthDatePicker
                                                date={form.endDate ? form.endDate : null}
                                                onDateChange={d => d && update('endDate', d)}
                                                height="40px"
                                            />
                                        </div>
                                    )}
                                </div>
                            </Field>

                            <Field title={t('Amount')} required>
                                {renderTextField(form.amount, v => update('amount', v), { type: 'number' })}
                            </Field>

                            <Field title={t('Description')} required>
                                {renderTextField(form.description, v => update('description', v))}
                            </Field>

                            {renderVatSection(form, update, isEditMode)}
                        </div>
                    </div>
                );

            /* ───────── ONE-TIME CHARGES ───────── */
            case 'oneTimeCharges':
                return (
                    <div className={s.section}>
                        <div className={s.grid}>
                            <Field title={t('Date')}>
                                <CustomDatePicker
                                    date={form.date ? form.date : null}
                                    onDateChange={d => d && update('date', d)}
                                    height="40px"
                                />
                            </Field>

                            <Field title={t('Amount')} required>
                                {renderTextField(form.amount, v => update('amount', v), { type: 'number' })}
                            </Field>

                            <Field title={t('Description')} required full>
                                {renderTextField(form.description, v => update('description', v))}
                            </Field>

                            {renderVatSection(form, update, isEditMode)}
                        </div>
                    </div>
                );

            /* ───────── PAYMENTS ───────── */
            case 'payments':
                // Show multiple payments interface for all clients
                if (!isEditMode) {
                    return (
                        <>
                            {/* Client Selection for Multiple Payments - Only show for leading business */}
                            {leadingBusiness && (
                                <div className={s.section}>
                                    <div className={s.grid}>
                                        <Field title={t('Client')} required>
                                            {renderClientSelect(
                                                selectedClientForMultiple,
                                                (_, newVal) => setSelectedClientForMultiple(newVal)
                                            )}
                                        </Field>
                                    </div>
                                </div>
                            )}

                            {/* Multiple Payments Section */}
                            <div className={s.section}>
                                <div className={s.sectionHeader}>
                                    <h3 className={s.sectionTitle}>
                                        {t('Payments')} ({multiplePayments.length})
                                    </h3>
                                    <button type="button" className={`${s.secondaryBtn} ${s.smallBtn}`} onClick={addPaymentRow}>
                                        <AddIcon fontSize="small" />
                                        {t('AddPayment')}
                                    </button>
                                </div>

                                {multiplePayments.map((payment, index) => (
                                    <div key={payment.id} className={s.paymentCard}>
                                        <div className={s.paymentCardHeader}>
                                            <span className={s.paymentBadge}>
                                                {t('Payment')} #{payment.id}
                                            </span>
                                            {multiplePayments.length > 1 && (
                                                <Tooltip title={t('Delete')}>
                                                    <IconButton
                                                        onClick={() => removePaymentRow(payment.id)}
                                                        size="small"
                                                        sx={{ color: '#ef476f' }}
                                                    >
                                                        <RemoveCircleOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </div>

                                        <div className={s.grid}>
                                            <Field title={t('Type')} required>
                                                {renderTypeSelect(payment.type, v => updateMultiplePayment(index, 'type', v))}
                                            </Field>

                                            <Field title={t('Date')}>
                                                <CustomDatePicker
                                                    date={payment.date ? payment.date : null}
                                                    onDateChange={d => d && updateMultiplePayment(index, 'date', d)}
                                                    height="40px"
                                                />
                                            </Field>

                                            <Field title={t('Amount')} required>
                                                {renderTextField(payment.amount, v => updateMultiplePayment(index, 'amount', v), { type: 'number' })}
                                            </Field>

                                            <Field title={t('Reference')}>
                                                {renderTextField(payment.reference, v => updateMultiplePayment(index, 'reference', v))}
                                            </Field>

                                            <Field title={t('Description')} required full>
                                                {renderTextField(payment.description, v => updateMultiplePayment(index, 'description', v))}
                                            </Field>

                                            {renderVatSection(
                                                payment,
                                                (field, value) => updateMultiplePayment(index, field, value),
                                                false,
                                                false
                                            )}

                                            {String(payment.type) === CHECK_TYPE_VALUE &&
                                                renderCheckFields(payment, (field, value) => updateMultiplePayment(index, field, value))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    );
                }

                // Single payment (edit mode)
                return (
                    <div className={s.section}>
                        <div className={s.grid}>
                            <Field title={t('Type')} required>
                                {renderTypeSelect(form.type, v => update('type', v))}
                            </Field>

                            <Field title={t('Date')}>
                                <CustomDatePicker
                                    date={form.date ? form.date : null}
                                    onDateChange={d => d && update('date', d)}
                                    height="40px"
                                />
                            </Field>

                            <Field title={t('Amount')} required>
                                {renderTextField(form.amount, v => update('amount', v), { type: 'number' })}
                            </Field>

                            <Field title={t('Reference')}>
                                {renderTextField(form.reference, v => update('reference', v))}
                            </Field>

                            <Field title={t('Description')} required full>
                                {renderTextField(form.description, v => update('description', v))}
                            </Field>

                            {renderVatSection(form, update, isEditMode)}

                            {String(form.type) === CHECK_TYPE_VALUE && renderCheckFields(form, update)}
                        </div>
                    </div>
                );

            /* ───────── default ───────── */
            default:
                return null;
        }
    };

    // Updated validation logic
    const saveDisabled = () => {
        if (itemType === 'payments' && !isEditMode) {
            return !validateMultiplePayments();
        }

        return !form.amount ||
            !form.description ||
            (isEditMode && (form?.receiptId || form?.invoiceId)) ||
            (leadingBusiness && !form.caseId) ||
            (itemType === 'payments' && !form.type?.toString());
    };

    const handleSave = async () => {
        if (itemType === 'payments' && !isEditMode) {
            // Handle multiple payments for all clients
            const payload = buildMultiplePaymentsPayload();
            await handleAddItem(payload);
            resetForm();
        } else {
            // Handle single item
            const payload = buildPayload();
            isEditMode ? await handleEditItem(payload) : await handleAddItem(payload);
            resetForm();
        }
    };

    if (!isOpen) return null;

    const showPaymentAdminActions =
        itemType === 'payments' && isEditMode && leadingBusiness && onDeletePayment && onCancelPaymentManually;
    const showMakeNegative =
        showPaymentAdminActions &&
        onSetPaymentAmountNegative &&
        (form.invoiceIsCanceled || form.receiptIsCanceled || form.isCanceledManually) &&
        Number(form.amount) > 0;
    const showDelete = isEditMode && itemType !== 'payments' && !(form?.receiptId || form?.invoiceId);

    return (
        <Portal>
            <div className={s.overlay} onClick={handleClose}>
                <div dir={direction} onClick={e => e.stopPropagation()} aria-labelledby="form-dialog-title" role="dialog">
                    <div className={s.modal}>
                        <div className={s.body}>
                            <div className={s.header}>
                                <Tooltip title={t('Cancel')}>
                                    <IconButton onClick={handleClose} className={s.closeIcon} aria-label={t('Cancel')}>
                                        <CancelIcon className={s.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div className={s.headerTitle} id="form-dialog-title">
                                    {t(isEditMode ? `Edit ${itemType}` : `Add ${itemType}`)}
                                    {itemType === 'payments' && !isEditMode && (
                                        <span className={s.headerSubtitle}>{t('MultiplePaymentsMode')}</span>
                                    )}
                                </div>
                                <div style={{ width: 40 }} />
                            </div>

                            <div className={s.scroll}>
                                {leadingBusiness && itemType !== 'payments' && (
                                    <div className={s.section}>
                                        <div className={s.grid}>
                                            <Field title={t('Client')} required>
                                                {renderClientSelect(
                                                    clientOptions.find(o => o.value === form.caseId) || null,
                                                    (_, newVal) => update('caseId', newVal?.value || '')
                                                )}
                                            </Field>
                                        </div>
                                    </div>
                                )}
                                {renderFields()}
                            </div>

                            <div className={s.footer}>
                                {showPaymentAdminActions && (
                                    <>
                                        <button type="button" className={s.dangerBtn} onClick={() => onDeletePayment(form)}>
                                            <DeleteIcon fontSize="small" />
                                            {t('DeletePayment')}
                                        </button>
                                        <button type="button" className={s.warningBtn} onClick={() => onCancelPaymentManually(form)}>
                                            <BlockIcon fontSize="small" />
                                            {t('CancelManually')}
                                        </button>
                                        {showMakeNegative && (
                                            <button type="button" className={s.ghostBtn} onClick={() => onSetPaymentAmountNegative(form)}>
                                                {t('MakeAmountNegative')}
                                            </button>
                                        )}
                                    </>
                                )}

                                {showDelete && (
                                    <button type="button" className={s.dangerBtn} onClick={() => handleDeleteItem(form.id)}>
                                        <DeleteOutlineIcon fontSize="small" />
                                        {t('Delete')}
                                    </button>
                                )}

                                <span className={s.footerSpacer} />

                                {isEditMode && form?.invoiceId && (
                                    <button type="button" className={s.secondaryBtn} onClick={() => viewInvoice(form.invoiceId)}>
                                        <ReceiptLongIcon fontSize="small" />
                                        {t('ViewInvoice')}
                                    </button>
                                )}

                                {isEditMode && form?.receiptId && (
                                    <button type="button" className={s.secondaryBtn} onClick={() => viewReceipt(form.receiptId)}>
                                        <ReceiptIcon fontSize="small" />
                                        {t('ViewReceipt')}
                                    </button>
                                )}

                                <button
                                    type="button"
                                    className={s.primaryBtn}
                                    onClick={handleSave}
                                    disabled={saveDisabled() || loading}
                                >
                                    {isEditMode
                                        ? t('Save')
                                        : itemType === 'oneTimeCharges' || itemType === 'monthlyCharges'
                                            ? t('AddCharge')
                                            : t('AddPayment')}
                                </button>
                            </div>
                        </div>

                        {loading && (
                            <div className={s.loadingOverlay}>
                                <CircularProgress size={44} thickness={4} className={s.spinner} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default AddNewCollectionItem;
